/**
 * Dashboard Module
 *
 * Handles dashboard-specific functionality including:
 * - Floor plan table management (seat guests, release table)
 * - Table details modal
 * - Occupancy refresh
 */

import { $, $$, ready } from '../utils/dom.js';
import { getToken } from '../utils/csrf.js';

// =============================================================================
// State
// =============================================================================

let currentTableId = null;
let tableDetailsModal = null;
let seatGuestsModal = null;
let releaseTableModal = null;

// =============================================================================
// Table Details Modal
// =============================================================================

/**
 * Show table details in modal
 * @param {string} tableId - Table ID
 */
export function showTableDetails(tableId) {
    currentTableId = tableId;

    const bodyEl = document.getElementById('tableDetailsBody');
    if (bodyEl) {
        bodyEl.innerHTML = '<div class="text-center"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>';
    }

    fetch('/dashboard/api/table/' + tableId)
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                renderTableDetails(data.data);
                if (tableDetailsModal) {
                    tableDetailsModal.show();
                }
            }
        })
        .catch(error => {
            console.error('Error fetching table details:', error);
            if (bodyEl) {
                bodyEl.innerHTML = '<div class="alert alert-danger">Failed to load table details</div>';
            }
        });
}

/**
 * Render table details in modal body
 * @param {Object} table - Table data
 */
function renderTableDetails(table) {
    const titleEl = document.getElementById('tableDetailsTitle');
    const bodyEl = document.getElementById('tableDetailsBody');

    if (titleEl) {
        titleEl.textContent = 'Table ' + table.table_number;
    }

    if (!bodyEl) return;

    let statusBadge = '';
    switch (table.status) {
        case 'occupied':
            statusBadge = '<span class="badge bg-orange">Occupied</span>';
            break;
        case 'reserved':
            statusBadge = '<span class="badge bg-yellow">Reserved</span>';
            break;
        case 'closed':
            statusBadge = '<span class="badge bg-red">Closed</span>';
            break;
        default:
            statusBadge = '<span class="badge bg-green">Available</span>';
    }

    let html = `
        <div class="row g-3">
            <div class="col-6">
                <label class="form-label">Table Number</label>
                <div class="form-control-plaintext"><strong>${table.table_number}</strong></div>
            </div>
            <div class="col-6">
                <label class="form-label">Capacity</label>
                <div class="form-control-plaintext"><strong>${table.capacity} seats</strong></div>
            </div>
            <div class="col-6">
                <label class="form-label">Status</label>
                <div class="form-control-plaintext">${statusBadge}</div>
            </div>
            <div class="col-6">
                <label class="form-label">Room</label>
                <div class="form-control-plaintext"><strong>${table.room_name || '--'}</strong></div>
            </div>
    `;

    if (table.guest_count) {
        html += `
            <div class="col-6">
                <label class="form-label">Guests</label>
                <div class="form-control-plaintext"><strong>${table.guest_count}</strong></div>
            </div>
        `;
    }

    if (table.occupancy_duration) {
        html += `
            <div class="col-6">
                <label class="form-label">Duration</label>
                <div class="form-control-plaintext"><strong>${table.occupancy_duration}</strong></div>
            </div>
        `;
    }

    html += '</div>';

    // Action buttons based on status
    html += '<div class="mt-4">';
    if (table.status === 'available' || table.status === 'reserved') {
        html += `<button type="button" class="btn btn-primary" onclick="openSeatGuestsModal()">Seat Guests</button>`;
    }
    if (table.status === 'occupied') {
        html += `<button type="button" class="btn btn-warning" onclick="openReleaseTableModal()">Release Table</button>`;
    }
    html += '</div>';

    bodyEl.innerHTML = html;
}

// =============================================================================
// Seat Guests Modal
// =============================================================================

/**
 * Open the seat guests modal
 */
export function openSeatGuestsModal() {
    if (tableDetailsModal) {
        tableDetailsModal.hide();
    }

    const guestCountInput = document.getElementById('guestCount');
    if (guestCountInput) {
        guestCountInput.value = '';
    }

    if (seatGuestsModal) {
        seatGuestsModal.show();
    }
}

/**
 * Submit seat guests form
 */
export function submitSeatGuests() {
    if (!currentTableId) return;

    const guestCount = document.getElementById('guestCount')?.value;

    fetch('/dashboard/api/table/' + currentTableId + '/seat', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getToken()
        },
        body: JSON.stringify({
            guest_count: parseInt(guestCount) || 1
        })
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                if (seatGuestsModal) {
                    seatGuestsModal.hide();
                }
                window.location.reload();
            } else {
                alert(data.message || 'Failed to seat guests');
            }
        })
        .catch(error => {
            console.error('Error seating guests:', error);
            alert('Failed to seat guests');
        });
}

// =============================================================================
// Release Table Modal
// =============================================================================

/**
 * Open the release table modal
 */
export function openReleaseTableModal() {
    if (tableDetailsModal) {
        tableDetailsModal.hide();
    }

    if (releaseTableModal) {
        releaseTableModal.show();
    }
}

/**
 * Submit release table form
 */
export function submitReleaseTable() {
    if (!currentTableId) return;

    fetch('/dashboard/api/table/' + currentTableId + '/release', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getToken()
        }
    })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                if (releaseTableModal) {
                    releaseTableModal.hide();
                }
                window.location.reload();
            } else {
                alert(data.message || 'Failed to release table');
            }
        })
        .catch(error => {
            console.error('Error releasing table:', error);
            alert('Failed to release table');
        });
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initDashboard() {
    // Initialize Bootstrap modals if present
    const tableDetailsModalEl = document.getElementById('tableDetailsModal');
    const seatGuestsModalEl = document.getElementById('seatGuestsModal');
    const releaseTableModalEl = document.getElementById('releaseTableModal');

    if (typeof bootstrap !== 'undefined') {
        if (tableDetailsModalEl) {
            tableDetailsModal = new bootstrap.Modal(tableDetailsModalEl);
        }
        if (seatGuestsModalEl) {
            seatGuestsModal = new bootstrap.Modal(seatGuestsModalEl);
        }
        if (releaseTableModalEl) {
            releaseTableModal = new bootstrap.Modal(releaseTableModalEl);
        }
    }

    // Set up button handlers
    const seatGuestsBtn = document.getElementById('seatGuestsBtn');
    const releaseTableBtn = document.getElementById('releaseTableBtn');

    if (seatGuestsBtn) {
        seatGuestsBtn.addEventListener('click', submitSeatGuests);
    }
    if (releaseTableBtn) {
        releaseTableBtn.addEventListener('click', submitReleaseTable);
    }
}

ready(initDashboard);

// Expose globally for onclick handlers
window.showTableDetails = showTableDetails;
window.openSeatGuestsModal = openSeatGuestsModal;
window.openReleaseTableModal = openReleaseTableModal;
window.submitSeatGuests = submitSeatGuests;
window.submitReleaseTable = submitReleaseTable;

export default {
    showTableDetails,
    openSeatGuestsModal,
    submitSeatGuests,
    openReleaseTableModal,
    submitReleaseTable
};
